import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrerenderSection, usePrerenderTrigger } from '~/hooks/use-prerender';
import { useScrollSpy } from '~/hooks/use-scroll-spy';
import { scrollToSectionById } from '~/hooks/use-scroll-spy-store';
import { HashUrlButton } from '~/modules/common/hash-url-button';
import { StickyBox } from '~/modules/common/sticky-box';
import { DocsPageHeader } from '~/modules/docs/docs-page-header';
import { TagOperationsList } from '~/modules/docs/operations/operation-detail';
import { ViewModeToggle } from '~/modules/docs/operations/view-mode-toggle';
import { operationsByTagQueryOptions, tagDetailsQueryOptions, tagsQueryOptions } from '~/modules/docs/query';
import { TagExpandLink } from '~/modules/docs/tag-expand-link';
import { TagOperationsTable } from '~/modules/docs/tag-operations-table';
import type { GenOperationSummary } from '~/modules/docs/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/modules/ui/card';
import { Collapsible, CollapsibleContent } from '~/modules/ui/collapsible';
import { queryClient } from '~/query/query-client';
import { cn } from '~/utils/cn';
import { getHashUrl } from '../hash-url';

function OperationsPage() {
  const { t } = useTranslation();
  const { operationTag: activeTag } = useSearch({ from: '/_public/_content/docs/operations' });

  const { prerender } = usePrerenderTrigger('operations');

  const { data: operationsByTag } = useSuspenseQuery(operationsByTagQueryOptions);
  const { data: allTags } = useSuspenseQuery(tagsQueryOptions);
  const tags = allTags.filter((t) => t.count > 0);

  const operationCount = tags.reduce((sum, t) => sum + t.count, 0);

  // Operation hashes are contributed by OperationDetail when rendered
  const tagSectionIds = tags.map((t) => `tag/${t.name}`);

  useScrollSpy(tagSectionIds);

  // Retry hash scrolling on mount to cover child registration races and collapsed content layout.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && tagSectionIds.includes(hash)) scrollToSectionById(hash);
    // Mount only. Re-running on tag changes would fight user scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="container">
        <DocsPageHeader title={t('c:operation', { count: 2 })} />
      </div>

      <StickyBox className="z-10 bg-background/60 backdrop-blur-xs" placeholderClassName="my-2" hideWhenOutOfView>
        <div className="container flex items-center gap-3 py-3">
          <ViewModeToggle />

          <span className="text-muted-foreground text-sm lowercase">
            {operationCount} {t('c:operation', { count: operationCount })}
          </span>
        </div>
      </StickyBox>

      <div className="container flex flex-col gap-12 lg:gap-20">
        {tags.map((tag) => (
          <TagSection
            key={tag.name}
            tag={tag}
            operations={operationsByTag[tag.name] || []}
            isOpen={activeTag === tag.name}
            onPrerender={() => {
              queryClient.prefetchQuery(tagDetailsQueryOptions(tag.name));
              prerender(tag.name);
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface TagSectionProps {
  tag: { name: string; description?: string; count: number };
  operations: GenOperationSummary[];
  isOpen: boolean;
  onPrerender: () => void;
}

/** Separate component so usePrerenderSection can be called per tag. */
function TagSection({ tag, operations, isOpen, onPrerender }: TagSectionProps) {
  // Defer page content mount by one frame so the sidebar can update first
  const [deferredIsOpen, setDeferredIsOpen] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setDeferredIsOpen(true));
    } else {
      setDeferredIsOpen(false);
    }
  }, [isOpen]);

  const { shouldMount, style } = usePrerenderSection('operations', tag.name, deferredIsOpen);

  // `enabled: isOpen` keeps tag details dormant until the user expands the section
  const { isFetching: detailsLoading } = useQuery({ ...tagDetailsQueryOptions(tag.name), enabled: isOpen });

  return (
    <Collapsible open={deferredIsOpen}>
      <Card id={`spy-tag/${tag.name}`} className={cn('scroll-mt-4 border-0', deferredIsOpen && 'rounded-b-none')}>
        <CardHeader className="group">
          <CardTitle className="gap-2 text-2xl leading-12">
            {tag.name}
            <HashUrlButton url={getHashUrl(`tag/${tag.name}`)} />
          </CardTitle>
          {tag.description && <CardDescription className="my-2 max-w-4xl text-base">{tag.description}</CardDescription>}
        </CardHeader>
        <CardContent className="rdg-readonly flex flex-col gap-4">
          <TagOperationsTable operations={operations} tagName={tag.name} onPrerender={onPrerender} />

          <TagExpandLink
            isOpen={isOpen}
            loading={isOpen && detailsLoading}
            to="."
            search={(prev) => ({ ...prev, operationTag: isOpen ? undefined : tag.name })}
            onMouseEnter={onPrerender}
          />
        </CardContent>
      </Card>

      {/* Operation details list, prerendered with content-visibility: hidden on hover */}
      {shouldMount && (
        <CollapsibleContent keepMounted>
          <div style={style}>
            <Suspense>
              <TagOperationsList operations={operations} />
            </Suspense>
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

export { OperationsPage };
